import { Button } from '@react-spectrum/s2';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { logger } from '@/logger';

import { editedBlobToFile } from './imageMarshalling';
import type { FireflyEditorLoader, FireflyTool } from './types';
import { useFireflyEditor } from './useFireflyEditor';

export interface FireflyImageEditorModalProps {
	isOpen: boolean;
	/** Source image (Azure SAS URL) to edit. */
	imageUrl: string | null;
	/** Close without saving. */
	onClose: () => void;
	/**
	 * Called with the edited image as a `File` when the user clicks Done. If the
	 * user made no edit, this is NOT called (caller leaves the image unchanged).
	 * Returns a promise so the modal can show a saving state and stay open on
	 * failure.
	 */
	onSave: (file: File) => Promise<void>;
	/** Filename base for the edited file (e.g. the artifact id). */
	saveBaseName?: string;
	enabledTools?: readonly FireflyTool[];
	/** Test seam: inject a fake editor loader. */
	loader?: FireflyEditorLoader;
	getAccessToken?: () => string | null;
	/** Current user id; enables backend-routed PROMPT generation. */
	userId?: string | null;
}

/**
 * Almost-fullscreen modal hosting the real Firefly image editor (ADO-1412).
 * Loads the editor on open, shows a spinner until it's interactive, renders an
 * error state if the bundle fails, and on Done reads the edited image and hands
 * it to `onSave` (reusing Bridgeway's existing upload + write-back).
 */
export function FireflyImageEditorModal({
	isOpen,
	imageUrl,
	onClose,
	onSave,
	saveBaseName,
	enabledTools,
	loader,
	getAccessToken,
	userId,
}: FireflyImageEditorModalProps) {
	const { containerRef, status, getEditedImageBlob } = useFireflyEditor({
		open: isOpen,
		imageUrl,
		enabledTools,
		loader,
		getAccessToken,
		userId,
	});
	const [isSaving, setIsSaving] = useState(false);

	// The modal component is never unmounted (the host always renders it; closing
	// only flips `isOpen` to false and returns null). Reset the saving state on
	// close so a reopened modal doesn't show a stale "Done" spinner.
	useEffect(() => {
		if (!isOpen) setIsSaving(false);
	}, [isOpen]);

	// Escape closes (unless mid-save).
	useEffect(() => {
		if (!isOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && !isSaving) onClose();
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [isOpen, isSaving, onClose]);

	const handleDone = useCallback(async () => {
		setIsSaving(true);
		try {
			const blob = await getEditedImageBlob();
			const file = editedBlobToFile(blob, saveBaseName);
			// No edit made → just close, nothing to write back.
			if (file !== null) {
				await onSave(file);
			}
			onClose();
		} catch (err) {
			logger.error('Failed to save edited image', err);
			// Keep the modal open so the user can retry or cancel.
			setIsSaving(false);
		}
	}, [getEditedImageBlob, onClose, onSave, saveBaseName]);

	if (!isOpen) return null;

	return createPortal(
		<div
			className="fixed inset-0 z-50 flex items-center justify-center
				bg-black/60"
			data-testid="firefly-editor-overlay"
			// While the save runs this dialog ignores Escape, and it covers the
			// prompt bar. Marking it inert lets the chat keep the key, so a run
			// stays stoppable during the upload.
			data-escape-inert={isSaving || undefined}
			role="dialog"
			aria-label="Edit image"
			aria-modal="true"
		>
			<div
				className="flex h-[92vh] w-[92vw] flex-col overflow-hidden rounded-xl
					bg-white"
			>
				<header
					className="flex items-center justify-between border-b
						border-neutral-200 px-5 py-3"
				>
					<div className="flex items-center gap-2">
						<img
							src="/logo-firefly.svg"
							alt=""
							aria-hidden="true"
							className="size-6"
						/>
						<h2 className="text-base font-semibold text-neutral-900">
							Edit image
						</h2>
					</div>
					<div className="flex items-center gap-2">
						<Button variant="secondary" onPress={onClose} isDisabled={isSaving}>
							Cancel
						</Button>
						<Button
							variant="accent"
							onPress={handleDone}
							isPending={isSaving}
							isDisabled={status !== 'ready'}
							data-testid="firefly-editor-done"
						>
							Done
						</Button>
					</div>
				</header>

				<div className="relative flex-1">
					{/*
					 * The editor element mounts here. The loader wraps this node in an
					 * `sp-theme` element at mount time (the bundle owns that Spectrum
					 * Web Component) and renders the editor in light theme to match
					 * firefly.adobe.com — so this chrome is light too.
					 */}
					<div
						ref={containerRef}
						data-testid="firefly-editor-container"
						className="absolute inset-0"
					/>

					{status === 'loading' && (
						<div
							className="absolute inset-0 grid place-items-center bg-white"
							data-testid="firefly-editor-loading"
						>
							<div className="flex flex-col items-center gap-3 text-neutral-600">
								<span
									className="size-8 animate-spin rounded-full border-2
										border-neutral-300 border-t-neutral-700"
								/>
								<span>Loading editor…</span>
							</div>
						</div>
					)}

					{status === 'error' && (
						<div
							className="absolute inset-0 grid place-items-center bg-white px-6
								text-center"
							data-testid="firefly-editor-error"
						>
							<div className="flex flex-col items-center gap-3 text-neutral-600">
								<p>The image editor could not be loaded.</p>
								<Button variant="secondary" onPress={onClose}>
									Close
								</Button>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
}
